import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useLocale } from '@/hooks/useLocale';
import { centsToUsdString } from '@/utils/money';

import styles from './styles.module.css';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=app.syron.mobile.syron';
const APP_STORE_URL = 'https://apps.apple.com/app/id0000000000';

type Preview = {
  amount_cents: number;
  currency: string;
  status: string;
  requires_keyword: boolean;
};

function apiBase(): string {
  const raw =
    import.meta.env.VITE_API_URL ?? import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
  return String(raw).replace(/\/$/, '');
}

export function MoneyLinkLandingPage(): JSX.Element {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { token = '' } = useParams<{ token: string }>();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const deepLink = useMemo(
    () => (token ? `syron://transfer/${encodeURIComponent(token)}` : ''),
    [token],
  );

  useEffect(() => {
    if (!token) {
      setError('missing_token');
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${apiBase()}/money-links/${encodeURIComponent(token)}`);
        if (!res.ok) {
          throw new Error(`http_${res.status}`);
        }
        const data = (await res.json()) as Preview;
        if (!cancelled) {
          setPreview(data);
        }
      } catch {
        if (!cancelled) {
          setError('load_failed');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className={styles.wrap}>
        <Spinner />
      </div>
    );
  }

  const isOpen = preview?.status === 'open';
  const unavailable = error != null || (preview != null && !isOpen);

  return (
    <div className={styles.wrap}>
      <Card className={styles.card} variant="light" padding="lg">
        <p className={styles.brand}>Syron</p>
        <h1 className={styles.title}>
          {t('money_link.landing_title', { defaultValue: 'Money for you' })}
        </h1>
        {isOpen && preview != null ? (
          <>
            <p className={styles.amount} aria-live="polite">
              {centsToUsdString(preview.amount_cents, locale)}
            </p>
            <p className={styles.body}>
              {t('money_link.landing_amount', {
                defaultValue: 'Open the app to receive this transfer.',
              })}
            </p>
          </>
        ) : null}
        {unavailable ? (
          <p className={styles.body} aria-live="polite">
            {t('money_link.landing_unavailable', {
              defaultValue: 'This link is no longer available.',
            })}
          </p>
        ) : null}
        <div className={styles.actions}>
          {deepLink !== '' && isOpen ? (
            <Button
              block
              size="lg"
              onClick={() => {
                window.location.href = deepLink;
              }}
            >
              {t('money_link.open_app', { defaultValue: 'Open in Syron' })}
            </Button>
          ) : null}
          <div className={styles.stores}>
            <a className={styles.storeLink} href={PLAY_STORE_URL}>
              {t('money_link.play_store', { defaultValue: 'Get on Google Play' })}
            </a>
            <a className={styles.storeLink} href={APP_STORE_URL}>
              {t('money_link.app_store', { defaultValue: 'Get on the App Store' })}
            </a>
          </div>
        </div>
      </Card>
    </div>
  );
}
